<template>
  <table class="hkl-attachment">
    <colgroup v-if="hasWidths">
      <col v-if="showIndex">
      <col>
      <col
        v-for="(col, index) of renderColumns"
        v-bind="col.width ? { width: col.width } : {}"
        :key="index"
      >
      <col
        v-if="hasDelete"
        width="32"
      >
    </colgroup>
    <thead
      v-if="hasHeader"
      class="hkl-attachment_header-group"
    >
      <tr class="hkl-attachment_header-before">
        <slot name="header-before" />
      </tr>
      <tr class="hkl-attachment_header">
        <th v-if="showIndex">#</th>
        <th />
        <th
          v-for="(col, index) of renderColumns"
          :key="index"
          :class="kebabCase(col.dataIndex)"
          :style="col.widthStyle"
        >
          <template v-if="col.title">{{ col.title }}</template>
        </th>
        <th
          v-if="hasDelete"
          width="32"
        />
      </tr>
      <tr class="hkl-attachment_header-after">
        <slot name="header-after" />
      </tr>
    </thead>
    <tbody
      v-for="(file, index) of fileList"
      :key="file.id"
      class="hkl-attachment_row-group"
    >
      <tr class="hkl-attachment_row-before">
        <td :colspan="mergedColumnsWidth">
          <slot
            name="row-before"
            :metaData="file.metaData"
            :file="file"
            :index="index"
          />
        </td>
        <td v-if="hasDelete" />
      </tr>
      <tr class="hkl-attachment_row">
        <td v-if="showIndex">
          {{ index + 1 }}
        </td>
        <td>
          <!-- #region Filename -->
          <div class="hkl-attachment_filename">
            <HKLDownloadLink
              v-if="file.canDownload"
              :doc="file"
            />
            <template v-else>{{ file.filename }}</template>
          </div>
          <!-- endregion Filename -->
          <!-- #region Description -->
          <HKLDescriptionPencilInput
            :value="file.description"
            :canEdit="file.canEdit"
            @close="(content) => updateDescriptionFn(file.id, content)"
          />
          <!-- endregion Description -->
          <slot
            name="file-after"
            :metaData="file.metaData"
            :file="file"
            :index="index"
          />
        </td>
        <!-- #region RenderColumns -->
        <template v-for="(col, index) of renderColumns">
          <td
            v-if="col && col.component"
            :key="index"
            align="center"
            :class="[kebabCase(col.dataIndex), col.class]"
            :style="col.widthStyle"
          >
            <component
              :is="col.componentFn(file.metaData[col.dataIndex], index, file)"
              v-if="!['a', 'span', 'p', 'div'].includes(col.componentFn(file.metaData[col.dataIndex], index, file))"
              v-model="file.metaData[col.dataIndex]"
              v-bind="col.attrsFn(file.metaData[col.dataIndex], index, file) || {}"
              v-fusioninputcheck="col.fusioninputcheckFn(file.metaData[col.dataIndex], index, file)"
              v-on="col.listenersFn(file.metaData[col.dataIndex], index, file) || {}"
            />
            <component
              :is="col.componentFn(file.metaData[col.dataIndex], index, file)"
              v-else
              v-bind="col.attrsFn(file.metaData[col.dataIndex], index, file) || {}"
              v-on="col.listenersFn(file.metaData[col.dataIndex], index, file) || {}"
            >{{ file.metaData[col.dataIndex] }}</component>
          </td>
        </template>
        <!-- #endregion RenderColumns -->
        <td
          v-if="hasDelete"
          align="center"
          width="32"
        >
          <HKLDeleteIcon
            v-if="file.canDelete"
            class="file-upload-delete"
            @click="() => deleteFile(file)"
          />
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import lodash from 'lodash'
import { computed, unref, onMounted, watch } from 'vue-demi'
import EventBus from '@/utils/eventBus'
import HKLDescriptionPencilInput from '@/controls/Input/HKLDescriptionPencilInput.vue'
import HKLDeleteIcon from '@/controls/Icon/HKLDeleteIcon.vue'
import { updateDescription, deleteDocument } from '@/api/docs/request'
import HKLDownloadLink from './HKLDownloadLink.vue'
import { useAttachmentList } from './HKLAttachment.hook'
import { parseBooleanFn } from './utils'

export const EVENTS = {
  UPDATE_FILES: 'updateFiles',
  DELETE_FILE: 'deleteFile',
}

/**
 * @type {import('vue').Component}
 */
const HKLAttachment = {
  components: {
    HKLDescriptionPencilInput,
    HKLDownloadLink,
    HKLDeleteIcon,
  },
  props: {
    sourceId: {
      type: String,
      required: true,
      validator (val) {
        return /^[a-f\d]{24}$/i.test(val)
      },
    },
    canEdit: {
      type: [Boolean, Function],
      default: true,
    },
    canDelete: {
      type: [Boolean, Function],
      default: true,
    },
    canDownload: {
      type: [Boolean, Function],
      default: true,
    },
    columns: {
      type: Array,
      default: () => [],
      validator (val) {
        return !val.find((v) => !(typeof v.dataIndex === 'string' && v.component))
      },
    },
    filter: {
      type: Function,
      default: (items) => items,
    },
    /**
     * @example String
     * :order="'asc'"
     * @example Array
     * :order="[['updateAt', 'id'], ['desc', 'asc']]"
     * @example Function
     * :order="(list) => list.sort()"
     */
    order: {
      type: [String, Array, Function],
      default: 'desc',
      validator (val) {
        const ORDERS = ['asc', 'desc']
        switch (typeof val) {
          case 'function': return true
          case 'string': return ORDERS.includes(val.toLowerCase())
          case 'object':
            if (!Array.isArray(val)) return false
            const [indexes, orders = []] = val
            if (
              indexes.find((index) => typeof index !== 'string') ||
              orders.find((o) => !ORDERS.includes(o))
            ) return false
            return true
        }
        return false
      },
    },
    showIndex: {
      type: Boolean,
      default: false,
    },
  },
  setup (props, { emit }) {
    // #region Source Data
    const {
      data: attachments,
      refresh: refreshAttachments,
      rawData: originAttachments,
      clearPendingMetaData,
    } = useAttachmentList(props.sourceId)
    const canEditFn = (file, index) => parseBooleanFn(props.canEdit, { fnArgs: [file, index] })
    const canDeleteFn = (file, index) => parseBooleanFn(props.canDelete, { fnArgs: [file, index] })
    const canDownloadFn = (file, index) => parseBooleanFn(props.canDownload, { fnArgs: [file, index] })
    const orderBy = (list = []) => {
      let iteratees = [['createdAt']]
      switch (typeof props.order) {
        case 'function': return props.order(list)
        case 'string':
          iteratees[1] = [props.order]
          break
        case 'object':
          iteratees = props.order
          break
      }
      return lodash.orderBy(list, ...iteratees)
    }
    const fileList = computed(() => {
      return orderBy(props.filter(unref(attachments)))
        .map((attachment, index) => (({
          ...attachment,
          canEdit: canEditFn(attachment, index),
          canDelete: canDeleteFn(attachment, index),
          canDownload: canDownloadFn(attachment, index),
        })))
    })
    const emitFiles = () => emit(EVENTS.UPDATE_FILES, unref(fileList))
    watch(() => fileList, () => emitFiles(), { deep: true })
    // #endregion Source Data
    // #region Columns
    const renderColumns = computed(() => parseColumns(props.columns))
    // #endregion Columns

    const update = async (id, appendObj) => {
      const file = unref(attachments).find((f) => f.id === id)
      if (file) {
        Object.entries(appendObj).forEach(([key, value]) => {
          file.metaData[key] = value
        })
      }
    }
    const changeFiles = computed(() => {
      return unref(fileList)
        .filter((file) => {
          const source = lodash.cloneDeep(file.metaData)
          const target = unref(originAttachments).find((attachment) => attachment.id === file.id).metaData
          return !lodash.isEqual(source, target)
        })
    })
    const hasChangeFiles = computed(() => !!unref(changeFiles).length)
    const save = async (attachMetadataCallback) => {
      const appendFn = (file) => {
        let obj = {}
        if (typeof attachMetadataCallback === 'function') obj = attachMetadataCallback(file)
        return obj
      }
      if (unref(hasChangeFiles)) {
        await Promise.all(unref(changeFiles).map((file) => updateDescription(file, { metaData: { ...file.metaData, ...appendFn(file) } })))
        await refreshAttachments()
        clearPendingMetaData()
      }
    }
    const deleteFile = async (file) => {
      await deleteDocument(file.id)
      EventBus.$emit(`controls.hkldDropzone.deleteItem.${props.sourceId}`, file)
      emit(EVENTS.DELETE_FILE, file)
      await refreshAttachments()
    }

    // #region Description Change
    const updateDescriptionFn = async (id, description) => {
      const file = unref(fileList).find((f) => f.id === id)
      if (file.description === description) return
      unref(fileList).forEach((file) => update(file.id, file.metaData))
      await updateDescription(Object.assign({}, file, { description }))
      await refreshAttachments()
    }
    // #endregion Description Change
    // #region Setup Env when init or sourceId change
    const setupListener = () => {
      if (props.sourceId) {
        clearPendingMetaData()
      }
    }
    watch(() => props.sourceId, setupListener)

    onMounted(() => {
      setupListener()
    })
    // #endregion Setup Env when init or sourceId change

    return {
      kebabCase: lodash.kebabCase,
      hasWidths: computed(() => unref(renderColumns).some(col => col.width)),
      hasHeader: computed(() => unref(renderColumns).some(col => col.title)),
      hasDelete: computed(() => unref(fileList).some(file => file.canDelete)),
      mergedColumnsWidth: computed(() => {
        return unref(renderColumns).length +
          (props.showIndex ? 1 : 0) + // index
          1 // filename
      }),
      updateDescriptionFn,
      fileList,
      renderColumns,
      update,
      save,
      deleteFile,
      clearPendingMetaData,
    }
  },
}

const parseColumns = (columns = []) => {
  return columns.map((col) => ({
    ...col,
    widthStyle: col.width
      ? { width: typeof col.width === 'number' ? `${col.width}px` : col.width }
      : undefined,
    componentFn: (val, index, file) => {
      if (typeof col.component === 'function') return col.component(val, index, file)
      return col.component
    },
    attrsFn: (val, index, file) => {
      if (typeof col.attrs === 'object') return col.attrs
      if (typeof col.attrs === 'function') return col.attrs(val, index, file)
    },
    listenersFn: (val, index, file) => {
      if (typeof col.listeners === 'object') return col.listeners
      if (typeof col.listeners === 'function') return col.listeners(val, index, file)
    },
    fusioninputcheckFn: (val, index, file) => {
      if (typeof col.fusioninputcheck === 'object') return col.fusioninputcheck
      if (typeof col.fusioninputcheck === 'function') return col.fusioninputcheck(val, index, file)
    },
  }))
}

export default HKLAttachment
</script>

<style lang="scss" scoped>
.hkl-attachment {
  border-width: 0;
  thead {
    border-width: 0;
    background-color: unset;
    th {
      font-weight: unset;
      text-transform: unset;
    }
  }
  td:has(&_filename) {
    overflow-wrap: anywhere;
  }
  &_row {
    &-before,
    &-after {
      td {
        padding-top: 0;
        padding-bottom: 0;
      }
    }
    &-before,
    &-after,
    & {
      border: 0;
      background-color: unset;
    }
    &-group {
      background-color: #fafbfc;
      & + & {
        border-top: 2px solid #ffffff;
      }
    }
    .file-upload-delete {
      cursor: pointer;
    }
  }
}
</style>
